import React from 'react';
import { Box, Button, Typography, Alert } from '@mui/material';
import AuthMobileCard from './AuthMobileCard';
import FaceCapture from './FaceCapture';
import {
  darkSecondaryButtonSx,
  darkAlertSx,
  LOGIN_ACCENT,
} from './loginDarkTheme';

export default function LoginFaceStep({
  infoMessage,
  errors,
  isSubmitting,
  otpFallbackAvailable = true,
  onCapture,
  onBack,
  onUseOtp,
  variant = 'desktop',
}) {
  const isDarkMobile = variant === 'mobile-dark';

  const banner = isDarkMobile ? (
    <Box
      sx={{
        mb: 2,
        p: 1.75,
        borderRadius: '14px',
        border: `1px solid rgba(76, 225, 182, 0.35)`,
        background: 'linear-gradient(135deg, rgba(76, 225, 182, 0.14) 0%, rgba(76, 225, 182, 0.05) 100%)',
      }}
    >
      <Typography variant="body2" sx={{ color: '#cbd5e1', fontSize: '0.85rem', lineHeight: 1.45 }}>
        Look at the camera to verify your identity with{' '}
        <Box component="span" sx={{ color: LOGIN_ACCENT, fontWeight: 700 }}>
          face recognition
        </Box>
        .
      </Typography>
    </Box>
  ) : (
    <Box
      sx={{
        p: 1.6,
        mb: 3,
        borderRadius: '12px',
        border: '1px solid rgba(76, 225, 182, 0.45)',
        background: 'linear-gradient(135deg, rgba(76, 225, 182, 0.12) 0%, rgba(76, 225, 182, 0.06) 100%)',
      }}
    >
      <Typography variant="body2" sx={{ color: '#263238', fontSize: '12.5px', lineHeight: 1.3, fontWeight: 600 }}>
        Verify your identity with face recognition to continue.
      </Typography>
    </Box>
  );

  const nonFieldErrors = errors?.non_field_errors;
  const errorMessage = Array.isArray(nonFieldErrors) ? nonFieldErrors.join(' ') : nonFieldErrors;

  const content = (
    <>
      {banner}
      {infoMessage && (
        <Alert severity="info" sx={isDarkMobile ? darkAlertSx : { mb: 2 }}>
          {infoMessage}
        </Alert>
      )}
      {errorMessage && (
        <Alert severity="error" sx={isDarkMobile ? darkAlertSx : { mb: 2 }}>
          {errorMessage}
        </Alert>
      )}
      <FaceCapture
        variant={isDarkMobile ? 'mobile-dark' : variant}
        onCapture={onCapture}
        isSubmitting={isSubmitting}
        captureLabel="Verify & sign in"
        helperText="Match the face you enrolled in your profile. Good lighting helps."
      />
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, mt: 2 }}>
        {otpFallbackAvailable && (
          <Button
            type="button"
            variant="text"
            onClick={onUseOtp}
            disabled={isSubmitting}
            sx={isDarkMobile ? darkSecondaryButtonSx : { textTransform: 'none' }}
          >
            Use email OTP instead
          </Button>
        )}
        <Button
          type="button"
          variant="text"
          onClick={onBack}
          disabled={isSubmitting}
          sx={isDarkMobile ? darkSecondaryButtonSx : { textTransform: 'none' }}
        >
          Back to sign in
        </Button>
      </Box>
    </>
  );

  if (isDarkMobile) {
    return <AuthMobileCard title="Face verification">{content}</AuthMobileCard>;
  }

  return (
    <Box sx={{ width: '100%', maxWidth: 420 }}>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 1, color: '#212121' }}>
        Face verification
      </Typography>
      <Typography variant="body2" sx={{ color: '#546e7a', mb: 3 }}>
        Complete sign-in with the face you registered in your profile.
      </Typography>
      {content}
    </Box>
  );
}
